/**
 * MP Phase 3 — detect per-parcel provider variance on the SED and emit
 * audit findings when worst vs mean tier differs by >= 1.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { logAuditEvent } from '../telemetry/auditTelemetry';

export interface ParcelVarianceFinding {
  domain: 'elevation' | 'flood' | 'seismic' | 'zoning' | 'habitat';
  message: string;
  meanTier: number;
  worstTier: number;
  parcelCount: number;
}

const DOMAINS: Array<{
  domain: ParcelVarianceFinding['domain'];
  sectionKey: keyof SiteEvidenceDocument;
  label: string;
}> = [
  { domain: 'flood', sectionKey: 'flood', label: 'FEMA flood zone' },
  { domain: 'seismic', sectionKey: 'seismic', label: 'Seismic design category' },
  { domain: 'elevation', sectionKey: 'siteProfile', label: 'Elevation' },
  { domain: 'zoning', sectionKey: 'parcel', label: 'Zoning' },
  { domain: 'habitat', sectionKey: 'environmental', label: 'Habitat / wetlands' },
];

export function detectParcelVariance(sed: SiteEvidenceDocument): ParcelVarianceFinding[] {
  if ((sed.parcelCount ?? 1) < 2) return [];

  const findings: ParcelVarianceFinding[] = [];

  for (const { domain, sectionKey, label } of DOMAINS) {
    const section = sed[sectionKey] as { parcelAggregate?: { varianceFlagged?: boolean; meanTier?: number; worstTier?: number } } | undefined;
    const meta = section?.parcelAggregate;
    if (!meta?.varianceFlagged) continue;

    const meanTier = meta.meanTier ?? 0;
    const worstTier = meta.worstTier ?? 0;
    if (worstTier - meanTier < 1) continue;

    findings.push({
      domain,
      message: `${label} varies across ${sed.parcelCount} parcels (mean tier ${meanTier}, worst tier ${worstTier}). Review per-parcel exposure before citing site-wide values.`,
      meanTier,
      worstTier,
      parcelCount: sed.parcelCount ?? 0,
    });
  }

  // Fallback: compare sed.parcels[] fields directly when section meta absent.
  if (findings.length === 0 && sed.parcels && sed.parcels.length >= 2) {
    const floodZones = new Set(sed.parcels.map(p => p.floodZone).filter(Boolean));
    if (floodZones.size > 1) {
      findings.push({
        domain: 'flood',
        message: `FEMA flood zones disagree across parcels: ${Array.from(floodZones).join(', ')}.`,
        meanTier: 0,
        worstTier: 1,
        parcelCount: sed.parcels.length,
      });
    }
    const sdcSet = new Set(sed.parcels.map(p => p.seismicSDC).filter(Boolean));
    if (sdcSet.size > 1) {
      findings.push({
        domain: 'seismic',
        message: `Seismic design categories disagree across parcels: ${Array.from(sdcSet).join(', ')}.`,
        meanTier: 0,
        worstTier: 1,
        parcelCount: sed.parcels.length,
      });
    }
  }

  return findings;
}

export function logParcelVarianceTelemetry(
  siteId: string,
  findings: ParcelVarianceFinding[],
): void {
  for (const f of findings) {
    logAuditEvent({
      type: 'per-parcel-variance-flagged',
      siteId,
      payload: { domain: f.domain, meanTier: f.meanTier, worstTier: f.worstTier },
    });
  }
}
